import { Icon } from '@iconify/react';
import {
  alpha,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BoostService } from '_apis_/boost';
import {
  CustomFile,
  Label,
  MIconButton,
  UploadFileListProps,
  UploadImagePreview
} from 'components';
import { ICurrentPreviewImages } from 'components/upload/UploadImagePreview';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { fData } from 'utils/formatNumber';
import { IImage } from '../../@types/boost';
import { ImageTypes } from '../../@types/boostGroup';

export default function UploadFileList({
  type,
  hasFile,
  fileArray,
  boostGroupImages,
  showPreview,
  showDefaultButton,
  onRemove,
  onDefault,
  ownerId,
  boostGroupId,
  openSnackBar,
  isSiteConf
}: UploadFileListProps) {
  const [previewFiles, setPreviewFiles] = useState<ICurrentPreviewImages>(fileArray[0]);
  const boostService = new BoostService();

  useEffect(() => {
    const selectedItem = boostGroupImages?.find((item) => item.type === type);
    setSelected(selectedItem?.imageId);
  }, [boostGroupImages]);

  const [selected, setSelected] = useState<number>();
  const [showModal, setShowModal] = useState<boolean>();

  const getFileData = (file: CustomFile | string) => {
    if (typeof file === 'string') {
      return {
        key: file
      };
    }

    return {
      key: file.id,
      name: file.name,
      size: file.imageSize,
      preview: file.preview
    };
  };

  const handleDefault = async (file: CustomFile) => {
    try {
      boostGroupImages?.find((image) => {
        if (image.type === ImageTypes.GROUP_LOGO && type === ImageTypes.GROUP_LOGO) {
          image.imageId = file.id;
        }
        if (image.type === ImageTypes.GROUP_BACKGROUND && type === ImageTypes.GROUP_BACKGROUND) {
          image.imageId = file.id;
        }
      });
      onDefault(file);
    } catch (error) {
      openSnackBar(false, 'Error marking as site');
    }
  };

  const OverlayStyle = styled('div')(({ theme }) => ({
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    position: 'absolute',
    backgroundColor: alpha(theme.palette.grey[900], 0.28)
  }));

  /**
   * Find the other image based on the other Array.
   */
  function findMatchingItemsById(fileImages: IImage[]): IImage[] {
    return fileImages.filter((item1) => {
      return boostGroupImages?.some((item2) => item1.id === item2.imageId);
    });
  }

  /**
   * Get the other images that is not part of the dropdown.
   * if the bg is open, get the logo, if the logo is open get the bg.
   */
  async function getOtherImage(type: ImageTypes): Promise<IImage> {
    if (ownerId) {
      const response = await boostService.getImages(ownerId, Number(boostGroupId), type);
      const matchingItems = findMatchingItemsById(response.data);
      if (matchingItems.length) {
        matchingItems[0].preview = matchingItems[0].imageUrl || matchingItems[0].logoUrl;
        return matchingItems[0];
      }
    }
    throw new Error('Image not found.');
  }

  async function openPreview(currentPreview: CustomFile, type: string) {
    let images;
    if (
      type === ImageTypes.GROUP_BACKGROUND ||
      type === ImageTypes.GROUP_PRICE ||
      type === ImageTypes.GROUP_HELP_PAGE_CONTENT
    ) {
      images = {
        logo: await getOtherImage(ImageTypes.GROUP_LOGO),
        bg: currentPreview
      };
    } else {
      images = {
        logo: currentPreview,
        bg: await getOtherImage(ImageTypes.GROUP_BACKGROUND)
      };
    }
    setPreviewFiles(images as ICurrentPreviewImages);
    setShowModal(true);
  }

  const closePreview = () => setShowModal(false);

  /**
   * The Api is not returning the name, just the file link, we need to extract the name from there.
   */
  const fileName = (url: string) => {
    const fileNameWithExtension = url.substring(url.lastIndexOf('/') + 1);
    return fileNameWithExtension.substring(0, fileNameWithExtension.lastIndexOf('.'));
  };

  return (
    <List disablePadding sx={{ ...(hasFile && { my: 3 }) }}>
      {showModal && (
        <UploadImagePreview show={showModal} onClose={closePreview} files={previewFiles} />
      )}
      <AnimatePresence>
        {fileArray.map((file, index) => {
          const { key, name, preview } = getFileData(file as CustomFile);
          if (showPreview) {
            return (
              <ListItem
                className={type + '-PreviewThumbnailContainer'}
                key={index + 'fileList_' + name}
                component={motion.div}
                sx={{
                  p: 0,
                  m: 0.5,
                  width: 180,
                  height: 180,
                  borderRadius: 1,
                  display: 'inline-flex'
                }}
                selected={selected === key}
              >
                {/* Selected warn */}
                {showDefaultButton && selected === key && (
                  <Label
                    variant="filled"
                    color={'error'}
                    sx={{
                      top: 5,
                      left: 10,
                      zIndex: 9,
                      position: 'absolute',
                      textTransform: 'uppercase'
                    }}
                  >
                    default
                  </Label>
                )}
                <Paper
                  variant="outlined"
                  component="img"
                  src={preview}
                  data-testid={`${ownerId}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    position: 'absolute'
                  }}
                />
                {selected === key && <OverlayStyle />}
                <Box sx={{ top: 6, right: 6, position: 'absolute' }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-evenly"
                  >
                    {!isSiteConf && (
                      <Tooltip title="Preview">
                        <MIconButton
                          size="small"
                          onClick={() => openPreview(file, type)}
                          sx={{
                            p: '2px',
                            color: 'common.white',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.72),
                            '&:hover': {
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.48)
                            }
                          }}
                        >
                          <Icon icon={'eva:eye-fill'} />
                        </MIconButton>
                      </Tooltip>
                    )}
                    {!isSiteConf && showDefaultButton && selected !== key && (
                      <Tooltip title="Set as default for this group">
                        <MIconButton
                          size="small"
                          data-testid={'setDefaultButton' + type + key}
                          onClick={() => handleDefault(file)}
                          sx={{
                            p: '2px',
                            color: 'common.white',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.72),
                            '&:hover': {
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.48)
                            }
                          }}
                        >
                          <Icon icon={'eva:checkmark-fill'} />
                        </MIconButton>
                      </Tooltip>
                    )}

                    {(selected !== key || isSiteConf) && (
                      <Tooltip title="Delete">
                        <MIconButton
                          size="small"
                          onClick={() => onRemove(file)}
                          sx={{
                            p: '2px',
                            color: 'common.white',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.72),
                            '&:hover': {
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.48)
                            }
                          }}
                        >
                          <Icon icon={'eva:close-fill'} />
                        </MIconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              </ListItem>
            );
          }
          return (
            <ListItem
              key={index + 'fileList_' + name}
              component={motion.div}
              sx={{
                my: 1,
                py: 0.75,
                px: 2,
                borderRadius: 1,
                border: (theme) => `solid 1px ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                '&:hover': { opacity: 0.72 }
              }}
              selected={selected === key}
            >
              <ListItemIcon>
                <Icon icon={'eva:file-fill'} width={28} height={28} />
              </ListItemIcon>
              <ListItemText
                primary={preview ? fileName(preview) : ''}
                secondary={fData(file.size || 0)}
                primaryTypographyProps={{ variant: 'subtitle2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <Stack direction="row" spacing={5} alignItems="center" justifyContent="space-evenly">
                {!isSiteConf && showDefaultButton && selected !== key ? (
                  <Tooltip title="Set as default for this group">
                    <MIconButton
                      edge="end"
                      sx={{
                        p: '2px',
                        color: 'common.white',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.72),
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.48)
                        }
                      }}
                      size="small"
                      onClick={() => handleDefault(file)}
                    >
                      <Icon icon={'eva:checkmark-fill'} />
                    </MIconButton>
                  </Tooltip>
                ) : !isSiteConf && selected === key ? (
                  <Label
                    variant="filled"
                    color={'error'}
                    sx={{
                      right: 50,
                      zIndex: 9,
                      position: 'absolute',
                      textTransform: 'uppercase'
                    }}
                  >
                    default
                  </Label>
                ) : null}

                {(selected !== key || isSiteConf) && (
                  <Tooltip title="Delete">
                    <MIconButton
                      edge="end"
                      sx={{
                        p: '2px',
                        color: 'common.white',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.72),
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.48)
                        }
                      }}
                      size="small"
                      onClick={() => onRemove(file)}
                    >
                      <Icon icon={'eva:close-fill'} />
                    </MIconButton>
                  </Tooltip>
                )}
              </Stack>
            </ListItem>
          );
        })}
      </AnimatePresence>
    </List>
  );
}
